import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import ItemCardAuctionName from '@/components/GridItemCard/Card/components/ItemCardAuctionName/ItemCardAuctionName';
import { ItemCardCountdown } from '@/components/GridItemCard/Card/components/ItemCardCountdown/ItemCardCountdown';
import ItemCardSaveIcon from '@/components/GridItemCard/Card/components/ItemCardSaveIcon/ItemCardSaveIcon';
import ItemCardTitle from '@/components/GridItemCard/Card/components/ItemCardTitle/ItemCardTitle';
import { ImageRow } from '@/components/ItemCard/ImageRow';
import { useIsBrowsingPage } from '@/hooks/useIsBrowsingPage';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useRecommendationTracking } from '@/hooks/useRecommendationTracking';
import { useAppSelector } from '@/redux/hooks';
import {
    getIsItemSummaryTimedPlusBiddingClosed,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsReserveMet,
    getItemSummaryIsSold,
    getItemSummaryStartTime,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardNavigationTrigger, useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics';
import { LotRecommendationTrigger } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { useItemCardNavigation } from '@/utils/amplitude/experiments/hooks/useItemCardNavigation';

import { ItemCardComponent, ItemCardInfo } from '../../../types/types';
import RegisterLinkOrPrice from './components/RegisterLinkOrPrice/RegisterLinkOrPrice';
import TimedBadgeContainer from './components/TimedBadgeContainer/TimedBadgeContainer';

const TimedPlusItemCard: ItemCardComponent = ({
    className,
    dimension,
    itemId,
    recommendationPosition,
    recommendationType,
}) => {
    const { pathname } = useLocation();
    const isCatalogPage = pathname.includes('/catalog');

    const [linkHover, setLinkHover] = useState(false);

    const saleStartTs = useAppSelector((state) => getItemSummaryStartTime(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const isTimedPlusBiddingClosed = useAppSelector((state) => getIsItemSummaryTimedPlusBiddingClosed(state, itemId));
    const isReserveMet = useAppSelector((state) => getItemSummaryIsReserveMet(state, itemId));
    const { onNavigateInitiated } = useItemCardNavigation(itemId);
    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const showStatusBanner = !pathname.includes('/c/') && (!isSold || isReserveMet) && !isPaused;

    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);
    const displayItemName = isMobile && (isSold || isPassed || isPaused || saleStartTs > Math.round(Date.now() / 1000));

    const isBrowsingPage = useIsBrowsingPage();

    const enterLinkHoverState = React.useCallback(() => {
        setLinkHover(true);
    }, []);

    const leaveLinkHoverState = React.useCallback(() => {
        setLinkHover(false);
    }, []);

    const onRecommendationClick = useRecommendationTracking({
        itemId,
        recommendationPosition,
        recommendationType,
    });

    return (
        <StyledArticle
            $dimension={dimension}
            className={className}
            data-testid={`timed-plus-item-card-${itemId}`}
        >
            <TimedPlusItemCardContainer $dimension={dimension}>
                <PlacedItemCardSaveIcon>
                    <ItemCardSaveIcon
                        itemId={itemId}
                        labeled={!isMobile && isBrowsingPage}
                    />
                </PlacedItemCardSaveIcon>
                <TimedBadgeContainer itemId={itemId} />
                <PlacedImageContainer
                    onMouseEnter={enterLinkHoverState}
                    onMouseLeave={leaveLinkHoverState}
                >
                    <ImageRow
                        itemId={itemId}
                        onNavigate={() => {
                            onNavigateInitiated(ItemCardNavigationTrigger.ImageClick);
                            onRecommendationClick(LotRecommendationTrigger.ImageClick);
                        }}
                        showBidStatus={showStatusBanner}
                        showImageWithHover={!isMobile}
                    />
                </PlacedImageContainer>

                <PlacedCatalogInformation data-testid="timed-plus-item-card.catalog-information">
                    {isTimedPlusBiddingClosed ? (
                        <BodySecondary>
                            <FormattedMessage id="lot_closed" />
                        </BodySecondary>
                    ) : (
                        <StyledCatalogTimeInformation data-testid="timed-plus-item-card.catalog-information.date-information">
                            <ItemCardCountdown
                                itemId={itemId}
                                onNavigate={() => onNavigateInitiated(ItemCardNavigationTrigger.CountdownClick)}
                            />
                            {!isCatalogPage && !isMobile && (
                                <ItemCardAuctionName
                                    itemId={itemId}
                                    onNavigate={() => {
                                        onNavigateInitiated(ItemCardNavigationTrigger.HouseNameClick);
                                        onRecommendationClick(LotRecommendationTrigger.HouseNameClick);
                                    }}
                                />
                            )}
                            {!isCatalogPage && isMobile && displayItemName && (
                                <ItemCardAuctionName
                                    itemId={itemId}
                                    onNavigate={() => {
                                        onNavigateInitiated(ItemCardNavigationTrigger.HouseNameClick);
                                        onRecommendationClick(LotRecommendationTrigger.HouseNameClick);
                                    }}
                                />
                            )}
                        </StyledCatalogTimeInformation>
                    )}
                </PlacedCatalogInformation>
                <PlacedItemCardTitle
                    isHovered={linkHover}
                    itemId={itemId}
                    onMouseEnter={enterLinkHoverState}
                    onMouseLeave={leaveLinkHoverState}
                    onNavigate={() => {
                        onNavigateInitiated(ItemCardNavigationTrigger.TitleClick);
                        onRecommendationClick(LotRecommendationTrigger.TitleClick);
                    }}
                    showLotNumber
                />
                <PlacedRegisterLinkOrPrice
                    itemId={itemId}
                    linkHover={linkHover}
                    onNavigate={() => {
                        onNavigateInitiated(ItemCardNavigationTrigger.PriceClick);
                    }}
                    onRegisterForAuctionClicked={() => trackRegisterForAuctionPressedAnalytics()}
                />
            </TimedPlusItemCardContainer>
        </StyledArticle>
    );
};

const info: ItemCardInfo = {
    dimensions: { desktop: 184, mobile: 164 },
    ItemCardComponent: TimedPlusItemCard,
};

export default info;

const PlacedImageContainer = styled.div`
    grid-area: img;
    position: relative;
    aspect-ratio: 1 / 1;
`;

const PlacedItemCardTitle = styled(ItemCardTitle)`
    grid-area: title;
`;

const PlacedRegisterLinkOrPrice = styled(RegisterLinkOrPrice)`
    grid-area: registerlinkorprice;
    height: 100%;
`;

const PlacedCatalogInformation = styled.div`
    grid-area: time;
    display: flex;
    justify-content: space-between;

    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StyledCatalogTimeInformation = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
`;

const PlacedItemCardSaveIcon = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
`;

const TimedPlusItemCardContainer = styled.section<{ $dimension: number }>`
    position: relative;
    width: inherit;
    min-width: 0;
    display: grid;
    grid-template-columns: ${({ $dimension }) => ($dimension ? `${$dimension}px` : '1fr')};
    grid-template-rows: min-content 22px min-content 4px min-content 4px min-content 24px;
    grid-template-areas:
        'img'
        '.'
        'time'
        '.'
        'title'
        '.'
        'registerlinkorprice'
        '.'
        'easyshipping'
        '.';
    justify-content: center;
`;

const StyledArticle = styled.article<{ $dimension }>`
    width: ${({ $dimension }) => ($dimension ? `${$dimension}px` : 'auto')};
`;
